import { get, set, createStore } from 'idb-keyval';
import { useEffect } from 'react';
import { useAuth } from '@/features/auth/components/AuthContext';
import {
  resolveCameraNote,
  resolveGenAudio,
  cameraNoteStableKey,
  genAudioStableKey,
  type GenAudioKind,
} from './genAudio';

/**
 * Offline audio pack for the tiny, immutable GEN_AUDIOS voice notes.
 *
 * The bucket is PRIVATE, so playback needs a signed URL (1h TTL). We must NOT key
 * the cache on the expiring signed URL — instead we cache the audio BYTES (Blob)
 * in IndexedDB keyed by the STABLE, extension-less object path (e.g.
 * 'LA/LA_cam_seeds'). On a hit we hand back a local `blob:` object-URL — instant,
 * offline-capable, and ZERO Supabase calls. On a miss we resolve a signed URL via
 * the existing resolvers (which already handle the .mp3.mp3 double-extension and
 * space-named-welcome realities), fetch the bytes, store them under the stable key,
 * and return a local object-URL. If IndexedDB is unavailable at any point we fall
 * straight back to the signed URL — playback degrades to today's behaviour, never
 * breaks.
 */

// Bump to force re-fetch of every clip (audio is otherwise treated as immutable /
// cache-forever). Keys are version-prefixed, so bumping simply orphans the old
// blobs (harmless; a few hundred KB) and re-downloads on next play/prefetch.
export const AUDIO_PACK_VERSION = 1;

const audioStore = createStore('agxl-audio-pack', 'blobs');

// Reuse ONE object-URL per stable key for the page's life (avoids per-play
// createObjectURL churn/leaks; bounded to the small pack size; auto-revoked on
// document unload).
const objectUrls = new Map<string, string>();

const cacheId = (stableKey: string) => `v${AUDIO_PACK_VERSION}:${stableKey}`;

async function readBlob(stableKey: string): Promise<Blob | null> {
  try {
    return (await get<Blob>(cacheId(stableKey), audioStore)) ?? null;
  } catch {
    return null; // IDB unavailable → treat as miss
  }
}

async function writeBlob(stableKey: string, blob: Blob): Promise<void> {
  try {
    await set(cacheId(stableKey), blob, audioStore);
  } catch {
    /* storage full / private mode / unavailable → skip caching, non-fatal */
  }
}

function toObjectUrl(stableKey: string, blob: Blob): string {
  const existing = objectUrls.get(stableKey);
  if (existing) return existing;
  const url = URL.createObjectURL(blob);
  objectUrls.set(stableKey, url);
  return url;
}

/**
 * Cache-first playable URL for a stable object key.
 * - HIT  → local `blob:` URL, NO Supabase call.
 * - MISS → `resolveSignedUrl()` (existing resolver), fetch bytes, store, return
 *          local URL. If the fetch/store fails, returns the signed URL directly
 *          (graceful degradation). Returns null only when nothing resolves.
 */
export async function getCachedAudioUrl(
  stableKey: string | null,
  resolveSignedUrl: () => Promise<string | null>,
): Promise<string | null> {
  if (!stableKey) return await safeResolve(resolveSignedUrl);

  // Session-reuse, then IndexedDB — both avoid any network on a warm/cold hit.
  const reused = objectUrls.get(stableKey);
  if (reused) return reused;

  const cached = await readBlob(stableKey);
  if (cached) return toObjectUrl(stableKey, cached);

  // Miss → resolve a signed URL and fetch the bytes.
  const signed = await safeResolve(resolveSignedUrl);
  if (!signed) return null;

  try {
    const resp = await fetch(signed);
    if (resp.ok) {
      const blob = await resp.blob();
      await writeBlob(stableKey, blob);
      return toObjectUrl(stableKey, blob);
    }
  } catch {
    /* network fetch failed → fall through to the signed URL below */
  }
  return signed; // degrade: usable now, will be cached on a later successful fetch
}

async function safeResolve(fn: () => Promise<string | null>): Promise<string | null> {
  try {
    return await fn();
  } catch {
    return null;
  }
}

/** Cache-first playable URL for a camera category-tab note. */
export function getCameraNoteUrl(
  tabkey: string,
  countryCode: string | null | undefined,
): Promise<string | null> {
  return getCachedAudioUrl(
    cameraNoteStableKey(tabkey, countryCode),
    () => resolveCameraNote(tabkey, countryCode),
  );
}

/**
 * Cache-first playable URL for a system GEN_AUDIOS clip. Accepts one kind or an
 * ordered candidate list (first that resolves wins); the cache key is the canonical
 * (underscore) form of the first candidate, so all candidates share one entry.
 */
export function getGenAudioUrl(
  kinds: GenAudioKind | GenAudioKind[],
  countryCode: string | null | undefined,
): Promise<string | null> {
  const list = Array.isArray(kinds) ? kinds : [kinds];
  return getCachedAudioUrl(
    genAudioStableKey(list[0], countryCode),
    async () => {
      for (const kind of list) {
        const url = await resolveGenAudio(kind, countryCode);
        if (url) return url;
      }
      return null;
    },
  );
}

// ── Pack pre-download ──────────────────────────────────────────────────────────

// The CCS camera tabkeys (crop + general). Aliased keys collapse in the stable-key
// map (fertilizer→fertilizing, disease→pests), so the pack de-duplicates itself.
// (Poultry tabkeys have no LA notes yet — see sprint debt.)
const PACK_CAMERA_TABKEYS = [
  'seeds', 'growth', 'flowers', 'harvest', 'fertilizer', 'pests', 'disease',
  'field', 'watering', 'weeding', 'land_prep', 'planting', 'other',
];

// System clips + their resolve candidates (welcome prefers underscore, falls back
// to the live space-named object).
const PACK_SYSTEM: GenAudioKind[][] = [
  ['location_confirmation'],
  ['tap_here_help'],
  ['increase_score_take_pic'],
  ['farm_advisor_welcome', 'farm advisor_welcome'],
  ['onfarm_note'],
];

const prefetchedCodes = new Set<string>();

/**
 * Pre-download the whole audio pack for a country code ONCE, in the background.
 * Idempotent (skips keys already cached, and won't re-run for a code this session).
 * Offline-tolerant: if the device is offline it caches nothing and stays eligible
 * to retry on the next login. Cheap (~1–2 MB total) and non-blocking — callers kick
 * it from an idle callback after login.
 */
export async function prefetchAudioPack(
  countryCode: string | null | undefined,
): Promise<void> {
  const code = (countryCode ?? '').trim().toUpperCase();
  if (!code || prefetchedCodes.has(code)) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return; // retry next login
  prefetchedCodes.add(code);

  // De-duplicate by stable key (collapses aliases).
  const jobs = new Map<string, () => Promise<string | null>>();
  for (const tabkey of PACK_CAMERA_TABKEYS) {
    const key = cameraNoteStableKey(tabkey, code);
    if (key && !jobs.has(key)) jobs.set(key, () => resolveCameraNote(tabkey, code));
  }
  for (const candidates of PACK_SYSTEM) {
    const key = genAudioStableKey(candidates[0], code);
    if (key && !jobs.has(key)) {
      jobs.set(key, async () => {
        for (const kind of candidates) {
          const url = await resolveGenAudio(kind, code);
          if (url) return url;
        }
        return null;
      });
    }
  }

  // Sequential + resilient: getCachedAudioUrl skips already-cached keys (no
  // re-download) and swallows per-item failures so one missing object can't abort
  // the pack.
  for (const [key, resolver] of jobs) {
    try {
      await getCachedAudioUrl(key, resolver);
    } catch {
      /* skip this clip, keep going */
    }
  }
}

/**
 * Kick the pack pre-download once the farmer + country code are known, on a low
 * priority (idle) callback shortly after login so nothing blocks the UI.
 */
export function useAudioPackPrefetch(): void {
  const { user } = useAuth();
  const code = user?.countryCode;
  useEffect(() => {
    if (!user?.id || !code) return;
    let cancelled = false;
    const kick = () => { if (!cancelled) void prefetchAudioPack(code); };

    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    if (w.requestIdleCallback) {
      idleId = w.requestIdleCallback(kick, { timeout: 4000 });
    } else {
      timeoutId = window.setTimeout(kick, 3000);
    }
    return () => {
      cancelled = true;
      if (idleId !== undefined && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [user?.id, code]);
}
